import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Eye, X, Loader2, RefreshCw, Zap } from 'lucide-react';
import { rb } from './theme';
import { sectionIsValid } from './sectionShapes';
import LivePreview from './LivePreview';
import ProfileStep from './steps/ProfileStep';
import ExperienceStep from './steps/ExperienceStep';
import EducationStep from './steps/EducationStep';
import SkillsStep from './steps/SkillsStep';
import ExtrasStep from './steps/ExtrasStep';
import ReviewStep from './steps/ReviewStep';

// Extras covers several backend sections; saved individually on Next.
const STEPS = [
  { label: 'Personal details', sections: ['profile'], Component: ProfileStep },
  { label: 'Work experience', sections: ['experience'], Component: ExperienceStep },
  { label: 'Education', sections: ['education'], Component: EducationStep },
  { label: 'Skills', sections: ['skills'], Component: SkillsStep },
  { label: 'Extras', sections: ['projects', 'certifications', 'languages', 'achievements'], Component: ExtrasStep },
  { label: 'Review & download', sections: [], Component: ReviewStep },
];

export default function WizardShell({ session }) {
  const {
    sessionId, wizardState, setWizardState, stepIndex, setStepIndex,
    saving, saveError, setSaveError, expired, saveSection, restartSession, generatePdf,
  } = session;
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [validationMsg, setValidationMsg] = useState(null);
  const [restarting, setRestarting] = useState(false);

  const step = STEPS[stepIndex];
  const progressPct = Math.round((stepIndex / (STEPS.length - 1)) * 100);

  const goTo = (idx) => {
    setValidationMsg(null);
    setSaveError(null);
    setStepIndex(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = async () => {
    for (const sectionType of step.sections) {
      if (!sectionIsValid(sectionType, wizardState)) {
        setValidationMsg(
          sectionType === 'profile'
            ? 'Please fill your name, email and phone number.'
            : 'Please complete the required fields (marked *) or remove empty entries.'
        );
        return;
      }
    }
    setValidationMsg(null);

    for (const sectionType of step.sections) {
      const ok = await saveSection(sectionType);
      if (!ok) return;
    }
    goTo(Math.min(stepIndex + 1, STEPS.length - 1));
  };

  const handleRestart = async () => {
    setRestarting(true);
    try {
      await restartSession();
    } finally {
      setRestarting(false);
    }
  };

  const StepComponent = step.Component;

  return (
    <div className="max-w-6xl mx-auto px-4 pb-28 md:pb-10">
      {/* Mobile progress header */}
      <div className="md:hidden sticky top-0 z-20 -mx-4 px-4 py-3 backdrop-blur-xl" style={{ backgroundColor: 'rgba(8,7,15,0.85)' }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-slate-100">{step.label}</span>
          <span className="text-xs font-semibold text-slate-400">Step {stepIndex + 1} of {STEPS.length}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div className="h-full rounded-full rb-prog" animate={{ width: `${progressPct}%` }} transition={{ type: 'tween', duration: 0.3 }} />
        </div>
      </div>

      <div className="md:grid md:grid-cols-[200px_1fr] lg:grid-cols-[220px_1fr_minmax(300px,380px)] md:gap-6 mt-4 md:mt-8">
        {/* Desktop left rail */}
        <aside className="hidden md:block">
          <nav className={`sticky top-24 space-y-1 p-3 rounded-2xl ${rb.glass}`}>
            {STEPS.map((s, i) => {
              const isDone = i < stepIndex;
              const isCurrent = i === stepIndex;
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => i <= stepIndex && goTo(i)}
                  disabled={i > stepIndex}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-colors
                    ${isCurrent ? rb.glowBtn : isDone ? 'text-slate-200 hover:bg-white/5' : 'text-slate-500 opacity-60 cursor-not-allowed'}`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] shrink-0 border
                    ${isCurrent ? 'border-white/60' : isDone ? 'bg-violet-500 text-white border-violet-500' : 'border-current'}`}>
                    {isDone ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  {s.label}
                </button>
              );
            })}
            <p className="px-3 pt-3 text-xs font-semibold text-violet-300">{progressPct}% complete</p>
          </nav>
        </aside>

        {/* Center form */}
        <main className="min-w-0">
          <div className={`rounded-3xl p-5 md:p-6 ${rb.glass}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-bold text-slate-100">{step.label}</h2>
            </div>

            {expired && (
              <div className="mb-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/40">
                <p className="text-sm text-amber-300 font-semibold mb-2">
                  Your session expired, but your answers are safe on this device.
                </p>
                <button type="button" onClick={handleRestart} disabled={restarting} className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl ${rb.glowBtn}`}>
                  {restarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Continue where I left off
                </button>
              </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div key={stepIndex} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.18 }}>
                <StepComponent
                  state={wizardState}
                  setState={setWizardState}
                  sessionId={sessionId}
                  onEditStep={goTo}
                  onGeneratePdf={generatePdf}
                />
              </motion.div>
            </AnimatePresence>

            {(validationMsg || saveError) && !expired && (
              <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-center justify-between gap-3">
                <span>{validationMsg || saveError}</span>
                {saveError && <button type="button" onClick={handleNext} className="font-bold underline shrink-0">Retry</button>}
              </div>
            )}

            {/* Desktop nav buttons */}
            {stepIndex < STEPS.length - 1 && (
              <div className="hidden md:flex items-center justify-between mt-6">
                <button type="button" onClick={() => goTo(Math.max(0, stepIndex - 1))} disabled={stepIndex === 0} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm ${rb.secondaryBtn}`}>
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="button" onClick={handleNext} disabled={saving} className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm ${rb.glowBtn}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {saving ? 'Saving…' : 'Next'} {!saving && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
        </main>

        {/* Desktop live preview */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-[11px] font-bold uppercase tracking-wide mb-2 text-slate-400">Live preview</p>
            <LivePreview state={wizardState} />
          </div>
        </aside>
      </div>

      {/* Mobile sticky bottom bar */}
      {stepIndex < STEPS.length - 1 && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 px-4 py-3 border-t border-white/10 flex gap-2 backdrop-blur-xl" style={{ backgroundColor: 'rgba(8,7,15,0.9)' }}>
          <button type="button" onClick={() => goTo(Math.max(0, stepIndex - 1))} disabled={stepIndex === 0} className={`px-4 py-3 rounded-xl text-sm ${rb.secondaryBtn}`} aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setShowMobilePreview(true)} className={`px-4 py-3 rounded-xl text-sm ${rb.secondaryBtn}`} aria-label="Preview resume">
            <Eye className="w-4 h-4" />
          </button>
          <button type="button" onClick={handleNext} disabled={saving} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm ${rb.glowBtn}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {saving ? 'Saving…' : 'Next'} {!saving && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      )}

      {/* Mobile full-screen preview */}
      <AnimatePresence>
        {showMobilePreview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="lg:hidden fixed inset-0 z-40 overflow-y-auto p-4 pt-14" style={{ backgroundColor: 'rgba(8,7,15,0.97)' }}>
            <button type="button" onClick={() => setShowMobilePreview(false)} className={`fixed top-3 right-4 z-50 p-2.5 rounded-full ${rb.glowBtn}`} aria-label="Close preview">
              <X className="w-5 h-5" />
            </button>
            <LivePreview state={wizardState} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
